import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../api/client.jsx';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import { useApiMutation } from '../../api/mutations';
import {
  taxRatesFixture,
  createTaxRateFixture,
  updateTaxRateFixture,
  deleteTaxRateFixture,
} from './fixture';
import { createDebugLogger } from '../../utils/debug';

const dlog = createDebugLogger('tax-rates');

/** GET /v1/admin/tax-rates — Defaults + DB-Overrides aus tax_rate_overrides */
export function useTaxRates() {
  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();

  return useQuery({
    queryKey: ['taxRates', fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return taxRatesFixture();
      }
      const res = await fetch('/v1/admin/tax-rates');
      dlog('/v1/admin/tax-rates raw', res);
      return {
        rates: res?.rates ?? [],
        sourceFile: res?.sourceFile ?? null,
        serverStartedAt: res?.serverStartedAt ?? null,
        raw: res,
      };
    },
    staleTime: 60_000,
  });
}

const INVALIDATE = ['taxRates'];

/** POST /v1/admin/tax-rates — Neuer Override (oder Drittland). */
export function useCreateTaxRate() {
  return useApiMutation({
    live: (fetch, body) =>
      fetch('/v1/admin/tax-rates', { method: 'POST', body }),
    fixture: (body) => createTaxRateFixture(body),
    invalidate: INVALIDATE,
    successMessage: (res, body) =>
      buildSuccessMessage(`${body.country}: Override angelegt.`, res),
  });
}

/** PUT /v1/admin/tax-rates/:country — Werte ändern. */
export function useUpdateTaxRate() {
  return useApiMutation({
    live: (fetch, { country, ...body }) =>
      fetch(`/v1/admin/tax-rates/${encodeURIComponent(country)}`, {
        method: 'PUT',
        body,
      }),
    fixture: ({ country, ...body }) => updateTaxRateFixture(country, body),
    invalidate: INVALIDATE,
    successMessage: (res, { country }) =>
      buildSuccessMessage(`${country}: Werte aktualisiert.`, res),
  });
}

/** DELETE /v1/admin/tax-rates/:country — Override entfernen. */
export function useDeleteTaxRate() {
  return useApiMutation({
    live: (fetch, { country }) =>
      fetch(`/v1/admin/tax-rates/${encodeURIComponent(country)}`, { method: 'DELETE' }),
    fixture: ({ country }) => deleteTaxRateFixture(country),
    invalidate: INVALIDATE,
    successMessage: (res, { country }) =>
      res?.fallback === 'default'
        ? `${country}: Override entfernt — EU-Default aktiv (binnen 60s).`
        : `${country}: Override entfernt — kein Fallback, Land ist nicht mehr definiert.`,
  });
}

function buildSuccessMessage(base, res) {
  if (Array.isArray(res?.warnings) && res.warnings.length > 0) {
    return `${base} Warnungen: ${res.warnings.join('; ')}`;
  }
  return base;
}
